import { getFileName } from "@/Utils/commonExportFunctions";
import { clampWeight } from "@/Utils/formatFunctions";
import { translate } from "@/translations/functions/hook";

import ExcelJS from "exceljs";
import { saveAs } from "file-saver";

import {
  COL_WIDTH,
  type ReportingExportProps,
  getDownstreamName,
  getPeriodExportLabel,
} from "./common";

const formatCsvValue = (value: number) => clampWeight(value, 2).toFixed(2);

const generateCsvFile = ({
  rows,
  wasteColumns,
  isIndividual,
  isPortfolio,
  showZones,
  nameLabel,
  timeframe,
  wasteTypeConfig,
  flatDownstreamData,
}: ReportingExportProps) => {
  const workbook = new ExcelJS.Workbook();
  const sheet = workbook.addWorksheet(
    getFileName("exportLabels:reportingSheetFileName", timeframe),
  );

  const columns: { header: string; key: string }[] = [
    ...(isIndividual
      ? [
          { header: nameLabel, key: "name" },
          ...(showZones
            ? [{ header: "reportingPage:reportingColumnZone", key: "zone" }]
            : []),
          { header: `${nameLabel} ID`, key: "id" },
        ]
      : [{ header: "reportingPage:reportingColumnPeriod", key: "period" }]),
    { header: translate("exportLabels:wasteType"), key: "wasteTypeName" },
    { header: translate("exportLabels:wasteTypeCode"), key: "wasteTypeCode" },
    { header: translate("exportLabels:amount"), key: "amount" },
    { header: translate("exportLabels:amountCO2"), key: "amountCO2" },
    // Downstream handling is configured per building, so it is left out in portfolio view.
    ...(isPortfolio
      ? []
      : [
          {
            header: translate("exportLabels:downstreamHandling"),
            key: "downstreamHandling",
          },
        ]),
  ];
  sheet.columns = columns.map((column) => ({
    ...column,
    width: COL_WIDTH,
    style: { font: { bold: true } },
  }));

  rows.forEach((row) => {
    wasteColumns.forEach((column) => {
      const amount = row.weights[column.code] ?? 0;
      if (!amount) return;

      const co2factor = wasteTypeConfig.find(
        (config) => config.wasteTypeCode === column.code,
      )?.co2factor;

      sheet.addRow({
        name: row.name,
        zone: row.zone ?? "",
        id: row.id ?? "",
        period: getPeriodExportLabel(row),
        wasteTypeName: column.label,
        wasteTypeCode: column.code,
        amount: formatCsvValue(amount),
        amountCO2: formatCsvValue(co2factor ? co2factor * amount : 0),
        downstreamHandling: getDownstreamName(column, flatDownstreamData),
      });
    });
  });

  return workbook;
};

export const exportReportingCsv = async (props: ReportingExportProps) => {
  const workbook = generateCsvFile(props);
  const csv = await workbook.csv.writeBuffer();
  saveAs(
    new Blob([csv], { type: "text/csv;charset=UTF-8" }),
    getFileName("reportingPage:reportingFileName", props.timeframe),
  );
};
